import { shallowReactive } from 'vue'

import type { ReportLayoutKey } from './layouts'

import { useEnv } from '@/shared/config'

// The typed page registry — v4's replacement for v3's client-side `linksName` overlay
// (reportStore.ts:220–254) + the per-page view wrappers (views/App/**). One row per report
// page drives: `routes.ts` (path/name/meta), the ReportPage props, the `report_id` query
// redirect, and module 15's page-context seam (v3 `setPageReports` died — page reports are
// DERIVABLE from the route name via `reportPageByRoute`, never stored).
//
// Route names and paths keep v3 PARITY exactly (deep links survive the cutover).

export interface ReportPageDefinition {
  /** v3 route name, verbatim — `?report_id` redirects and nav targets depend on it. */
  routeName: string
  /** Path under the authenticated '/' parent (v3 parity). */
  path: string
  /** Open to vertical-owned layouts registered through `registerReportLayout`. */
  layoutKey: ReportLayoutKey
  /** Availability-gate ids (v3 view `report-ids`): any valid → page renders. */
  reportIds: number[]
  /**
   * `?report_id=N` values that land on this page (v3 `linksName` route targets). Defaults to
   * `reportIds` when omitted; RFM diverges (gate 67, linksName routed 53 here).
   */
  queryReportIds?: number[]
  /** Module-15 recommendation scope (v3 `recom-ids`); defaults to the valid gate ids. */
  recomIds?: number[]
  navKey: string
  titleKey: string
  isNotTab?: boolean
  updatedDateId?: number
  updatedTab?: string
  headerTitleKey?: string
  headerSubtitleKey?: string
}

/** 08's own pages are registrations like any other — they just run first, at module eval. */
const SEED_REPORT_PAGES: readonly ReportPageDefinition[] = [
  {
    routeName: 'StoreAnalysis',
    path: 'app-analytics/store-analysis',
    layoutKey: 'STORE_ANALYSIS',
    reportIds: [58],
    navKey: 'storeAnalysis',
    titleKey: 'reports.pages.storeAnalysis',
    isNotTab: true,
  },
  {
    routeName: 'DemographicAnalysis',
    path: 'audience/demographic-analysis',
    layoutKey: 'DEMOGRAPHIC_ANALYSIS',
    reportIds: [70],
    recomIds: [70],
    navKey: 'demographicAnalysis',
    titleKey: 'reports.pages.demographicAnalysis',
  },
  {
    routeName: 'BudgetAllocation',
    path: 'budget-performance/budget-allocation',
    layoutKey: 'BUDGET_ALLOCATION',
    reportIds: [6, 68, 66],
    // 19 (Meta) and 45 (TikTok) are query-only claims, inherited when their explorer pages retired
    // (ORP-18435). `?report_id=19` used to resolve to `MetaAdAnalysis`; with that registration gone
    // the id would resolve to nothing and `registerReportQueryCallbacks` treats an unknown id as a
    // no-op, silently stranding the visitor wherever they already were. This page is where those
    // dimensions were consolidated, so it is where the deep link belongs. Query-only on purpose:
    // `reportIds` drives what the page fetches, and that is unchanged.
    queryReportIds: [6, 68, 66, 19, 45],
    navKey: 'budgetAllocation',
    titleKey: 'reports.pages.budgetAllocation',
  },
  {
    routeName: 'CreativeDashboard',
    path: 'creative-dashboard',
    layoutKey: 'CREATIVE_DASHBOARD',
    reportIds: [13],
    navKey: 'creativeDashboard',
    titleKey: 'reports.pages.creativeDashboard',
    headerTitleKey: 'reports.creativeDashboard.header.title',
    headerSubtitleKey: 'reports.creativeDashboard.header.subtitle',
  },
  {
    routeName: 'ProductPerformance',
    path: 'ecommerce/product-performance',
    layoutKey: 'PRODUCT_PERFORMANCE',
    reportIds: [17, 48],
    navKey: 'productPerformance',
    titleKey: 'reports.pages.productPerformance',
  },
  {
    routeName: 'HotelAdsAnalysis',
    path: 'travel/hotel-ads-analysis',
    layoutKey: 'HOTEL_ADS',
    reportIds: [69],
    navKey: 'hotelAds',
    titleKey: 'reports.pages.hotelAds',
    isNotTab: true,
  },
]

// Page context is read from Vue computeds (`usePageTitle` and the insights seam). A vertical may
// register from its own lazy route chunk, so the registry must repair readers that ran first.
const pages = shallowReactive(new Map<string, ReportPageDefinition>())

/** Guarded like the layout registry: before bootstrap validation, fail loud in development/CI. */
function isProduction(): boolean {
  try {
    return useEnv().MODE === 'production'
  } catch {
    return false
  }
}

function seedOwnPages(): void {
  for (const page of SEED_REPORT_PAGES) pages.set(page.routeName, page)
}
seedOwnPages()

/**
 * Register a report page's pure-data context. Verticals keep ownership of their route component;
 * this seam contributes the metadata shared infrastructure needs for `?report_id`, document title
 * and insights. It deliberately has no seal: unlike router records, these lookups happen live.
 *
 * Idempotent by identity for HMR. A different page claiming an existing route name or path is a
 * composition bug: throw outside production; in production warn and let the last registrant win.
 *
 * @returns an idempotent unregister for this exact registration.
 */
export function registerReportPage(page: ReportPageDefinition): () => void {
  const byName = pages.get(page.routeName)
  const byPath = [...pages.values()].find((existing) => existing.path === page.path)
  if (byName === page && (byPath === undefined || byPath === page)) return () => {}

  const collisions = [byName, byPath].filter(
    (item): item is ReportPageDefinition => item !== undefined && item !== page,
  )
  if (collisions.length > 0) {
    const key = byName !== undefined ? `route name "${page.routeName}"` : `path "${page.path}"`
    const message =
      `[reports] duplicate report page ${key} — route names and paths must be unique across ` +
      `every registrant (08 owns ${SEED_REPORT_PAGES.length} seeds)`
    if (!isProduction()) throw new Error(message)
    console.warn(`${message}; last registration wins`)
    for (const collision of new Set(collisions)) pages.delete(collision.routeName)
  }

  pages.set(page.routeName, page)
  return () => {
    if (pages.get(page.routeName) === page) pages.delete(page.routeName)
  }
}

/** Every registered page in deterministic registration order. */
export function registeredReportPages(): readonly ReportPageDefinition[] {
  return [...pages.values()]
}

/** Module-15 seam: the page context for a route name (replaces v3 `setPageReports`). */
export function reportPageByRoute(routeName: string): ReportPageDefinition | null {
  return pages.get(routeName) ?? null
}

/**
 * `?report_id` → route-name map (v3 `linksName` + router/index.js:159–164). First page to
 * claim an id wins; ids whose v3 target page has not been ported yet resolve to nothing.
 */
export function reportRouteForReportId(reportId: number): string | null {
  for (const page of pages.values()) {
    if ((page.queryReportIds ?? page.reportIds).includes(reportId)) return page.routeName
  }
  return null
}

/** Test isolation: drop every vertical registration and restore 08's seed pages. */
export function resetReportPageRegistry(): void {
  pages.clear()
  seedOwnPages()
}
